import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format, isValid } from "date-fns";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";

type OrderChange = {
  id: number;
  source: "TitleQ" | "Select" | "Select log";
  dateTime: string;
  userName: string;
  action: string;
  priorDetails: string | null;
  submittedChanges: string | null;
};

function actionLabel(action: string) {
  const parts = action.split("/").filter(Boolean);
  const name = parts.pop() || "Order change";
  const area = parts.pop();
  const commonActions: Record<string, string> = {
    create: "Create order",
    setordervalues: "Update order fields",
    addordercontact: "Add order contact",
    updateordercontact: "Update order contact",
    deleteordercontact: "Delete order contact",
    updateclosinginformation: "Update closing information",
    setsalesreps: "Update sales reps",
    addledgernote: "Add ledger note",
    editledgerduedate: "Update ledger due date",
    updateledgerassignedto: "Update ledger assignment"
  };
  const readable = commonActions[name.toLowerCase()] ||
    name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ");
  return area ? `${readable} · ${area}` : readable;
}

function formatDetails(value: string | null) {
  if (!value) return "Not available";
  try {
    const parsed = JSON.parse(value, (_key, item) => {
      if (typeof item !== "string" || !/^[\s]*[\[{]/.test(item)) return item;
      try { return JSON.parse(item); } catch { return item; }
    });
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
}

function formatTimestamp(value: string) {
  const date = new Date(value);
  return isValid(date) ? `${format(date, "MMM d, yyyy 'at' h:mm a")} ET` : value;
}

export function OrderHistoryDrawer({ open, orderNumber, onClose }: {
  open: boolean;
  orderNumber: string;
  onClose: () => void;
}) {
  const [changes, setChanges] = useState<OrderChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !orderNumber) return;
    const controller = new AbortController();
    setChanges([]);
    setQuery("");
    setError(false);
    setLoading(true);
    axios.get<OrderChange[]>("/api/AnalyticsLogging/orderchanges", {
      params: { orderNumber },
      signal: controller.signal
    }).then(({ data }) => {
      setChanges([...data].sort((a, b) => b.dateTime.localeCompare(a.dateTime) || b.id - a.id));
    }).catch(() => {
      if (!controller.signal.aborted) setError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [open, orderNumber]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return changes;
    return changes.filter((change) =>
      [change.userName, change.source, change.action, change.priorDetails, change.submittedChanges]
        .some((value) => value?.toLowerCase().includes(search))
    );
  }, [changes, query]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 600 }, maxWidth: "100%" } }}>
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Order history</Typography>
              <Typography variant="body2" color="text.secondary">{orderNumber}</Typography>
            </Box>
            <IconButton aria-label="Close order history" onClick={onClose} size="small">
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
          {changes.length > 0 && (
            <TextField
              fullWidth
              size="small"
              placeholder="Filter history"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              sx={{ mt: 1.5 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" width={18} /></InputAdornment> }}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={24} /></Stack>
          ) : error ? (
            <Alert severity="error">Unable to load order history. Close and reopen to try again.</Alert>
          ) : changes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No history has been recorded for this order yet.</Typography>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No history matches your filter.</Typography>
          ) : (
            <Stack spacing={0.5}>
              {filtered.map((change) => (
                <Accordion key={`${change.source}-${change.id}`} disableGutters elevation={0} sx={{ borderBottom: 1, borderColor: "divider", "&:before": { display: "none" } }}>
                  <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" width={18} />} sx={{ px: 0, minHeight: 56 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>{change.source === "TitleQ" ? actionLabel(change.action) : change.action}</Typography>
                      <Typography variant="body2" color="text.secondary">{formatTimestamp(change.dateTime)} · {change.userName || "Unknown user"} · {change.source}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                    {([["Prior details", change.priorDetails], ["Submitted changes", change.submittedChanges]] as const).map(([label, value]) => (
                      <Box key={label} sx={{ mt: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
                        <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 1, bgcolor: "background.neutral", fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 350, overflow: "auto" }}>
                          {formatDetails(value)}
                        </Box>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
